import { useEffect, useId } from 'react';
import { create } from 'zustand';

/**
 * One open overlay that hosts trade UI (trade drawer, sell drawer) for a market.
 * `token` identifies this acquisition (so a stale release never removes a later
 * one); `owner` identifies the overlay instance across re-acquisitions.
 */
export type TradeOverlaySlot = { token: number; owner: string; marketAddress: string };

/**
 * Open trade overlays, in the order they were opened (the last one is on top).
 * On mobile the global transaction toast hides for a record only while the
 * topmost overlay hosts the record's market: that overlay shows the recovery
 * actions instead (the top-placed toast would cover its title and tabs). A
 * record whose overlay is covered by another market's overlay, or of a market
 * with no open overlay, keeps the toast.
 * A list, not a flag, so one overlay closing never unhides another's.
 */
type TradeOverlayState = {
  slots: TradeOverlaySlot[];
  /** Registers an open overlay for `marketAddress`; the returned release is idempotent. */
  acquire: (owner: string, marketAddress: string) => () => void;
};

let nextToken = 0;

export const useTradeOverlayStore = create<TradeOverlayState>(set => ({
  slots: [],
  acquire: (owner, marketAddress) => {
    const token = ++nextToken;
    set(state => ({ slots: [...state.slots, { token, owner, marketAddress: marketAddress.toLowerCase() }] }));
    let released = false;
    return () => {
      if (released) return;
      released = true;
      set(state => ({ slots: state.slots.filter(slot => slot.token !== token) }));
    };
  },
}));

/**
 * The overlay that shows the recovery actions for `marketAddress`: the topmost
 * open overlay, and only when it hosts that market. Undefined means the toast
 * keeps the actions (no overlay of that market, or it is covered by another).
 */
export const topmostOwner = (slots: TradeOverlaySlot[], marketAddress: string | undefined) => {
  const top = slots.at(-1);
  return marketAddress && top?.marketAddress === marketAddress.toLowerCase() ? top.owner : undefined;
};

/**
 * Holds a trade-overlay slot for `marketAddress` while `active` is true and a
 * market is known. Returns this overlay's owner id (for `topmostOwner`).
 */
export const useTradeOverlaySlot = (active: boolean, marketAddress: string | undefined) => {
  const owner = useId();
  const market = marketAddress?.toLowerCase();
  useEffect(() => {
    if (!active || !market) return;
    return useTradeOverlayStore.getState().acquire(owner, market);
  }, [active, market, owner]);
  return owner;
};
